import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ClientProviderEvents, type EventDetails } from '@openfeature/core';
import { NOOP_PROVIDER, OpenFeature } from '@openfeature/web-sdk';
import { combineLatest, fromEventPattern, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { User } from '../authenticatedUser/types';
import { UserService } from '../user.service';

@Injectable({
    providedIn: 'root'
})
export class DynamicFeatureFlagService {
    private readonly providerReady$ = fromEventPattern<EventDetails<ClientProviderEvents.Ready>>(
        (handler) => OpenFeature.getClient().addHandler(ClientProviderEvents.Ready, handler),
        (handler) => OpenFeature.getClient().removeHandler(ClientProviderEvents.Ready, handler)
    );

    public readonly error$ = fromEventPattern<EventDetails<ClientProviderEvents.Error>>(
        (handler) => OpenFeature.getClient().addHandler(ClientProviderEvents.Error, handler),
        (handler) => OpenFeature.getClient().removeHandler(ClientProviderEvents.Error, handler)
    );

    constructor(private userService: UserService) {
        this.error$.pipe(take(1)).subscribe((error) => {
            console.warn('DynamicFeatureFlagService: Provider error, switching to deafult flags', error);
            OpenFeature.setProvider(NOOP_PROVIDER);
        });

        combineLatest([
            this.userService.user$.pipe(
                filter((user) => !!user),
                takeUntilDestroyed()
            ),
            this.providerReady$.pipe(take(1))
        ]).subscribe(([user, _providerReady]) => {
            console.debug('DynamicFeatureFlagService: Provider ready', _providerReady);
            this.setContext({ user });
        });
    }

    public isFeatureEnabled$(flagKey: string): Observable<boolean> {
        return this.providerReady$.pipe(
            // Flagsmith Provider ignores the passed default value and forces `false`
            map(() => OpenFeature.getClient().getBooleanValue(flagKey, false)),
            distinctUntilChanged()
        );
    }

    public getFeatureNumberValue$(flagKey: string, defaultValue: number): Observable<number> {
        return this.providerReady$.pipe(
            map(() => OpenFeature.getClient().getNumberValue(flagKey, defaultValue)),
            distinctUntilChanged()
        );
    }

    private setContext(context: PlatformContext): Promise<void> {
        return OpenFeature.setContext({
            targetingKey: context.user.email,
            traits: {
                org_type: context.user.organization?.type,
                org_id: context.user.organization?._id,
                user_email: { value: context.user.email, transient: true }
            }
        });
    }
}

interface PlatformContext {
    user: User;
}
